const DEFAULT_MESSAGE = 'Masz niezapisane zmiany. Czy na pewno chcesz opuścić stronę?';
const DEFAULT_TITLE = 'Niezapisane zmiany';
const DEFAULT_DESCRIPTION = 'Opuszczenie tego widoku spowoduje utratę zmian, które nie zostały jeszcze zapisane.';

function createUnsavedChangesRegistry() {
    const entries = new Map();
    let beforeUnloadBound = false;
    let suppressBeforeUnloadUntil = 0;
    let activeDialog = null;

    function pruneDisconnectedEntries() {
        entries.forEach((state, id) => {
            if (state.element && !state.element.isConnected) {
                entries.delete(id);
            }
        });
    }

    function isIgnored(state, ignoreElement) {
        return Boolean(ignoreElement && state.element && ignoreElement.contains(state.element));
    }

    function hasDirtyChanges(options = {}) {
        pruneDisconnectedEntries();

        return Array.from(entries.values()).some((state) => state.dirty && !isIgnored(state, options.ignoreElement));
    }

    function getMessage() {
        return DEFAULT_MESSAGE;
    }

    function escapeHtml(value) {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }

    function handleBeforeUnload(event) {
        if (Date.now() < suppressBeforeUnloadUntil || !hasDirtyChanges()) {
            return;
        }

        event.preventDefault();
        event.returnValue = getMessage();

        return getMessage();
    }

    function bindBeforeUnload() {
        if (beforeUnloadBound) {
            return;
        }

        window.addEventListener('beforeunload', handleBeforeUnload);
        beforeUnloadBound = true;
    }

    function setDirty(id, dirty) {
        const state = entries.get(id);
        if (!state) {
            return;
        }

        state.dirty = Boolean(dirty);
    }

    function markFormSaved(form) {
        if (!form) {
            return;
        }

        entries.forEach((state) => {
            if (state.element && form.contains(state.element)) {
                state.dirty = false;
                state.markSaved?.();
            }
        });
    }

    function destroyDialog(dialog) {
        if (!dialog) {
            return;
        }

        dialog.remove();
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('overflow');
        activeDialog = null;
    }

    function showConfirmDialog(options = {}) {
        if (activeDialog) {
            activeDialog.remove();
            activeDialog = null;
        }

        return new Promise((resolve) => {
            const dialog = document.createElement('div');
            const title = escapeHtml(options.title || DEFAULT_TITLE);
            const description = escapeHtml(options.description || DEFAULT_DESCRIPTION);
            const confirmText = escapeHtml(options.confirmText || 'Opuść widok');
            const cancelText = escapeHtml(options.cancelText || 'Zostań');

            dialog.className = 'modal';
            dialog.setAttribute('data-modal-root', '');
            dialog.setAttribute('aria-hidden', 'false');
            dialog.setAttribute('role', 'dialog');
            dialog.setAttribute('aria-modal', 'true');
            dialog.style.cssText = 'position:fixed;inset:0;z-index:2400;overflow:auto;padding:clamp(1rem,4vh,3.5rem) 1rem 1rem;';

            dialog.innerHTML = `
                <div data-modal-backdrop style="position:absolute;inset:0;background:rgba(15,23,42,.52);backdrop-filter:blur(3px);"></div>
                <div data-modal-dialog style="position:relative;width:min(460px,calc(100vw - 2rem));max-width:460px;margin:0 auto;">
                    <div data-modal-content style="display:flex;flex-direction:column;background:var(--white-bg);color:var(--text-color);border:1px solid var(--border-color);border-radius:1rem;overflow:hidden;box-shadow:0 20px 50px rgba(15,23,42,.18);">
                        <div style="display:flex;align-items:flex-start;gap:.85rem;padding:1.1rem 1.1rem .85rem;border-bottom:1px solid var(--border-color);">
                            <span style="display:inline-flex;align-items:center;justify-content:center;width:2.25rem;height:2.25rem;border-radius:.85rem;background:rgba(245,158,11,.12);color:#d97706;flex-shrink:0;">
                                <i class="bi bi-exclamation-triangle"></i>
                            </span>
                            <div style="min-width:0;">
                                <h5 style="margin:0;font-size:1.05rem;font-weight:700;line-height:1.3;color:var(--text-color);">${title}</h5>
                                <p style="margin:.35rem 0 0;font-size:.9rem;line-height:1.55;color:var(--text-muted);">${description}</p>
                            </div>
                        </div>
                        <div style="display:flex;justify-content:flex-end;gap:.6rem;padding:1rem 1.1rem;background:var(--hover-bg);">
                            <button type="button" data-unsaved-cancel class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-border tw-bg-surface tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-text tw-transition hover:tw-bg-surface-muted">${cancelText}</button>
                            <button type="button" data-unsaved-confirm class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-danger/20 tw-bg-danger tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-transition hover:tw-opacity-90">${confirmText}</button>
                        </div>
                    </div>
                </div>
            `;

            const close = (result) => {
                destroyDialog(dialog);
                resolve(result);
            };

            dialog.querySelector('[data-unsaved-cancel]')?.addEventListener('click', () => close(false));
            dialog.querySelector('[data-unsaved-confirm]')?.addEventListener('click', () => close(true));
            dialog.querySelector('[data-modal-backdrop]')?.addEventListener('click', () => close(false));
            dialog.addEventListener('keydown', (event) => {
                if (event.key === 'Escape') {
                    close(false);
                }
            });

            document.body.appendChild(dialog);
            document.body.classList.add('modal-open');
            document.body.style.overflow = 'hidden';
            activeDialog = dialog;

            window.setTimeout(() => dialog.querySelector('[data-unsaved-cancel]')?.focus(), 0);
        });
    }

    async function confirmNavigation(options = {}) {
        if (!hasDirtyChanges(options)) {
            return true;
        }

        return showConfirmDialog(options);
    }

    function bindModalLinksGuard() {
        if (document.documentElement.dataset.unsavedModalLinksGuardBound) {
            return;
        }

        document.documentElement.dataset.unsavedModalLinksGuardBound = '1';

        document.addEventListener('click', async (event) => {
            const link = event.target.closest?.('a.modallink, button.modallink');

            if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }

            if (!hasDirtyChanges()) {
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();

            if (await confirmNavigation()) {
                window.jQuery?.(link).ajaxmodal?.('create');
            }
        }, true);
    }

    return {
        register(id, element, options = {}) {
            if (!id) {
                return null;
            }

            let registryId = id;
            let suffix = 1;
            while (entries.has(registryId)) {
                suffix += 1;
                registryId = `${id}:${suffix}`;
            }

            bindBeforeUnload();
            entries.set(registryId, {
                element,
                dirty: false,
                markSaved: typeof options.markSaved === 'function' ? options.markSaved : null,
            });

            return {
                setDirty: (dirty) => setDirty(registryId, dirty),
                markSaved: () => {
                    setDirty(registryId, false);
                    entries.get(registryId)?.markSaved?.();
                },
                unregister: () => entries.delete(registryId),
            };
        },
        hasDirtyEditors: hasDirtyChanges,
        hasDirtyChanges,
        markFormSaved,
        suppressBeforeUnload(ms = 3000) {
            suppressBeforeUnloadUntil = Math.max(suppressBeforeUnloadUntil, Date.now() + ms);
        },
        confirmNavigation,
        bindModalLinksGuard,
    };
}

window.Taskello = window.Taskello || {};
window.Taskello.unsavedChanges = window.Taskello.unsavedChanges || createUnsavedChangesRegistry();
window.Taskello.unsavedChanges.bindModalLinksGuard();
